import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RefreshCw, Plus, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import useConfig from '../../hooks/useConfig';
import { formatCurrency } from './utils';

// Component to display the list of refunds for a reservation
export function RefundsList({ reservation, onRefresh }) {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [totalRefunded, setTotalRefunded] = useState(0);
  
  const { options: configOptions } = useConfig(['REFUND_REASON', 'REFUND_METHOD', 'REFUND_STATUS', 'FLUX_TYPE']);
  const refundReasons = configOptions.REFUND_REASON || [];
  const refundMethods = configOptions.REFUND_METHOD || [];
  const refundStatuses = configOptions.REFUND_STATUS || [];
  const fluxTypes = configOptions.FLUX_TYPE || [];

  const fetchRefunds = async () => {
    if (!reservation?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/refunds/reservation/${reservation.id}`);
      setRefunds(res.data.refunds || []);
      setTotalRefunded(res.data.total_refunded || 0);
    } catch (error) {
      console.error('Error fetching refunds:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, [reservation?.id]);

  const handleDelete = async (refundId) => {
    if (!window.confirm('Supprimer ce remboursement ?')) return;
    try {
      await api.delete(`/refunds/${refundId}`);
      toast.success('Remboursement supprimé');
      fetchRefunds();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const getReasonLabel = (code) => refundReasons.find(r => r.code === code)?.label || code;
  const getMethodLabel = (code) => refundMethods.find(m => m.code === code)?.label || code;
  const getStatusOption = (code) => refundStatuses.find(s => s.code === code);
  const getFluxLabel = (code) => fluxTypes.find(f => f.code === code)?.label || code;

  if (!reservation) return null;

  return (
    <div className="space-y-4">
      {/* Header with total */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-4 h-4 text-orange-600" />
          <span className="font-medium">Remboursements</span>
          {totalRefunded > 0 && (
            <Badge variant="destructive" className="ml-2">
              -{formatCurrency(totalRefunded, reservation.currency)}
            </Badge>
          )}
        </div>
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Nouveau
        </Button>
      </div>

      {/* Refunds list */}
      {loading ? (
        <div className="text-center py-4 text-muted-foreground">Chargement...</div>
      ) : refunds.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground text-sm">
          Aucun remboursement enregistré
        </div>
      ) : (
        <div className="space-y-2">
          {refunds.map((refund) => {
            const statusOption = getStatusOption(refund.status);
            return (
              <div 
                key={refund.id} 
                className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-900"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-red-600">
                        -{formatCurrency(refund.amount, refund.currency)}
                      </span>
                      <Badge 
                        variant="outline"
                        style={{
                          backgroundColor: statusOption?.color ? `${statusOption.color}15` : undefined,
                          borderColor: statusOption?.color,
                          color: statusOption?.color
                        }}
                      >
                        {statusOption?.label || refund.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                      <p>
                        <span className="font-medium">Motif:</span> {getReasonLabel(refund.reason)}
                      </p>
                      <p>
                        <span className="font-medium">Méthode:</span> {getMethodLabel(refund.method)} ({getFluxLabel(refund.type_flux)})
                      </p>
                      <p>
                        <span className="font-medium">Date:</span> {format(new Date(refund.refund_date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </p>
                      {refund.notes && (
                        <p className="italic">{refund.notes}</p>
                      )}
                    </div>
                  </div>
                  {refund.status === 'PENDING' && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(refund.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Refund Dialog */}
      <AddRefundDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        reservation={reservation}
        refundReasons={refundReasons}
        refundMethods={refundMethods}
        fluxTypes={fluxTypes}
        onSuccess={() => {
          fetchRefunds();
          onRefresh?.();
        }}
      />
    </div>
  );
}

// Dialog to add a new refund
export function AddRefundDialog({ 
  open, 
  onOpenChange, 
  reservation, 
  refundReasons = [], 
  refundMethods = [],
  fluxTypes = [],
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    reason: '',
    method: '',
    type_flux: '',
    notes: ''
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        amount: '',
        reason: refundReasons[0]?.code || '',
        method: refundMethods[0]?.code || '',
        type_flux: refundMethods[0]?.metadata?.default_flux || fluxTypes[0]?.code || 'CASH_CAISSE',
        notes: ''
      });
    }
  }, [open, refundReasons, refundMethods, fluxTypes]);

  // Update type_flux when method changes
  const handleMethodChange = (methodCode) => {
    const method = refundMethods.find(m => m.code === methodCode);
    setFormData(prev => ({
      ...prev,
      method: methodCode,
      type_flux: method?.metadata?.default_flux || prev.type_flux
    }));
  };

  const handleSubmit = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!formData.reason) {
      toast.error('Motif requis');
      return;
    }
    if (!formData.method) {
      toast.error('Méthode requise');
      return;
    }

    setLoading(true);
    try {
      await api.post('/refunds', {
        reservation_id: reservation.id,
        amount: parseFloat(formData.amount),
        currency: reservation.currency,
        reason: formData.reason,
        method: formData.method,
        type_flux: formData.type_flux,
        notes: formData.notes,
        status: 'COMPLETED'
      });
      toast.success('Remboursement enregistré');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  // Calculate max refundable amount
  const totalPaid = reservation?.payments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-orange-600" />
            Nouveau remboursement
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info banner */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Réservation #{reservation?.reservation_number || reservation?.id}</p>
                <p className="text-amber-700">
                  Montant total: {formatCurrency(reservation?.total_amount, reservation?.currency)}
                </p>
                {totalPaid > 0 && (
                  <p className="text-amber-700">
                    Déjà encaissé: {formatCurrency(totalPaid, reservation?.currency)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label>Montant à rembourser *</Label>
            <div className="relative">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="pr-16"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {reservation?.currency || 'MAD'}
              </span>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Motif du remboursement *</Label>
            <Select value={formData.reason} onValueChange={(v) => setFormData(prev => ({ ...prev, reason: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un motif" />
              </SelectTrigger>
              <SelectContent>
                {refundReasons.map((reason) => (
                  <SelectItem key={reason.code} value={reason.code}>
                    <div className="flex items-center gap-2">
                      {reason.color && (
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: reason.color }} />
                      )}
                      {reason.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Method */}
          <div className="space-y-2">
            <Label>Méthode de remboursement *</Label>
            <Select value={formData.method} onValueChange={handleMethodChange}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une méthode" />
              </SelectTrigger>
              <SelectContent>
                {refundMethods.map((method) => (
                  <SelectItem key={method.code} value={method.code}>
                    <div className="flex items-center gap-2">
                      {method.color && (
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: method.color }} />
                      )}
                      {method.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type flux */}
          <div className="space-y-2">
            <Label>Type de flux</Label>
            <Select value={formData.type_flux} onValueChange={(v) => setFormData(prev => ({ ...prev, type_flux: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un flux" />
              </SelectTrigger>
              <SelectContent>
                {fluxTypes.map((flux) => (
                  <SelectItem key={flux.code} value={flux.code}>
                    <div className="flex items-center gap-2">
                      {flux.color && (
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: flux.color }} />
                      )}
                      {flux.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optionnel)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Informations complémentaires..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-orange-600 hover:bg-orange-700">
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              'Enregistrer le remboursement'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RefundsList;
